import Badge from '@material-ui/core/Badge';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { animated, useSpring } from 'react-spring';
import useStyles from './DragLayer.styles';
import { DragItem, ItemTypes } from './DragItemTypes';

export interface DragLayerProps {
  renderDragItem: (nodeId: string, itemType: ItemTypes) => ReactNode;
}

export const DragLayer: FC<DragLayerProps> = ({ renderDragItem }) => {
  const classes = useStyles();

  const {
    item,
    itemType,
    clientOffset,
    sourceClientOffset,
    initialClientOffset,
    isDragging,
  } = useDragLayer((monitor) => ({
    item: monitor.getItem() as DragItem,
    itemType: monitor.getItemType(),
    clientOffset: monitor.getClientOffset(),
    sourceClientOffset: monitor.getSourceClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  const dragDistance = useRef(0);

  useEffect(() => {
    if (isDragging) {
      dragDistance.current = Math.max(
        dragDistance.current,
        initialClientOffset && clientOffset
          ? Math.max(
              Math.abs(initialClientOffset.x - clientOffset.x),
              Math.abs(initialClientOffset.y - clientOffset.y),
            )
          : 0,
      );
    } else {
      dragDistance.current = 0;
    }
  });

  const shouldOffsetDragItems =
    initialClientOffset &&
    dragDistance.current > 15 &&
    item &&
    item.boundingRect;

  const pointerOffsetY =
    shouldOffsetDragItems && initialClientOffset
      ? initialClientOffset.y -
        item.boundingRect.y +
        (item.stack.length > 1 ? 24 : 12)
      : 0;

  const pointerOffsetX =
    shouldOffsetDragItems && initialClientOffset
      ? initialClientOffset.x - item.boundingRect.x + 12
      : 0;

  const props = useSpring({
    transform: `translate(${pointerOffsetX}px, ${pointerOffsetY}px)`,
    from: { transform: `translate(0px, 0px)` },
  });

  if (
    !isDragging ||
    !sourceClientOffset ||
    !Object.values(ItemTypes).includes(itemType as ItemTypes)
  ) {
    return null;
  }

  return (
    <div className={classes.root}>
      {item.stack.length > 1 && (
        <div
          style={
            clientOffset
              ? {
                  position: 'absolute',
                  top: clientOffset.y + 12,
                  left: clientOffset.x + 12,
                }
              : {}
          }
        >
          <Badge color="primary" badgeContent={item.stack.length}>
            <div />
          </Badge>
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          top: sourceClientOffset.y,
          left: sourceClientOffset.x,
        }}
      >
        <animated.div style={props}>
          {item.stack.slice(0, 3).map((nodeId, i) => {
            return (
              <div
                key={nodeId}
                style={{
                  position: 'absolute',
                  top: i * 5,
                  left: i * -5,
                  zIndex: item.stack.length - i,
                  width: item.boundingRect.width,
                  height: item.boundingRect.height,
                }}
              >
                {renderDragItem(nodeId, itemType as ItemTypes)}
              </div>
            );
          })}
        </animated.div>
      </div>
    </div>
  );
};

export default DragLayer;
